import type { Reducer } from '@reduxjs/toolkit';
import { createAction } from '@reduxjs/toolkit';
import ReactGA4 from 'react-ga4';
import type { DeepReadonly } from 'ts-essentials';

import type { AppState } from '../../customer/redux';
import type { setPreviousPath } from '../../customer/redux/users';
import { SET_PREVIOUS_PATH } from '../../customer/redux/users';
import type { CustomerAction } from '../customerAction';

declare global {
  interface Window {
    ga?: any;
    JANE_GOOGLE_ANALYTICS_ENHANCED_ECOMMERCE_ENABLED: boolean;
  }
}

const APPLICATION_OPEN_MODAL = 'application/open-modal';

const APPLICATION_ADD_GOOGLE_ANALYTICS_INSTANCE =
  'application/add-google-analytics-instance';

type OpenModalParams =
  | { goBack?: () => void; isCheckoutAlert?: boolean; name: 'cartAlerts' }
  | { name: 'login' }
  | { name: 'logout' }
  | { name: 'replaceCart' }
  | {
      id: number | string;
      isMember: boolean;
      name: 'toggleCustomersMembership';
    };

export const openModal = createAction<
  {
    disallowClose?: boolean;
  } & OpenModalParams,
  typeof APPLICATION_OPEN_MODAL
>(APPLICATION_OPEN_MODAL);

export const addGoogleAnalyticsInstance = createAction<
  {
    trackingId: string;
  },
  typeof APPLICATION_ADD_GOOGLE_ANALYTICS_INSTANCE
>(APPLICATION_ADD_GOOGLE_ANALYTICS_INSTANCE);

const APPLICATION_SET_JANE_DEVICE_ID = 'application/set-jane-device-id';

export const setJaneDeviceId = createAction<
  { janeDeviceId: string },
  typeof APPLICATION_SET_JANE_DEVICE_ID
>(APPLICATION_SET_JANE_DEVICE_ID);

const APPLICATION_SET_MIXPANEL_DISTINCT_ID =
  'application/set-mixpanel-distinct-id';

export const setMixpanelDistinctId = createAction<
  { mixpanelDistinctId: string },
  typeof APPLICATION_SET_MIXPANEL_DISTINCT_ID
>(APPLICATION_SET_MIXPANEL_DISTINCT_ID);

export const APPLICATION_CLOSE_MODAL = 'application/close-modal';
export const closeModal = createAction<void, typeof APPLICATION_CLOSE_MODAL>(
  APPLICATION_CLOSE_MODAL
);

export type ApplicationActions =
  | ReturnType<typeof openModal>
  | ReturnType<typeof closeModal>
  | ReturnType<typeof setPreviousPath>
  | ReturnType<typeof setJaneDeviceId>
  | ReturnType<typeof setMixpanelDistinctId>
  | ReturnType<typeof addGoogleAnalyticsInstance>;

type ApplicationState = DeepReadonly<{
  googleAnalyticsInstance: string | null;
  isModalOpen: boolean;
  janeDeviceId: string | undefined;
  mixpanelDistinctId: string | undefined;
  modalName: string;
  modalOptions: any;
  notificationShown: boolean;
}>;

const getInitialState = (): ApplicationState => ({
  isModalOpen: false,
  modalName: '',
  modalOptions: {},
  notificationShown: false,
  janeDeviceId: undefined,
  mixpanelDistinctId: undefined,
  googleAnalyticsInstance: null,
});

const initializeEcommerceGa = function (
  trackingId: string,
  clientId?: string,
  referrer?: string
) {
  if (trackingId && trackingId.startsWith('UA')) return;

  ReactGA4.initialize(trackingId, {
    testMode: process.env['NODE_ENV'] === 'test',
    gaOptions: {
      cookieFlags: 'SameSite=None; Secure',
      cookieDomain: 'auto',
    },
  });

  try {
    const trackers = window?.ga?.getAll() || [];
    trackers.forEach((tracker: any) => tracker.set('referrer', referrer));
    // eslint-disable-next-line no-empty
  } catch {
  } finally {
    if (referrer) {
      Object.defineProperty(document, 'referrer', {
        get: () => referrer,
      });
    }
  }
};

const removeGa = function () {
  if (window.ga) {
    window.ga('remove');
  }
};

export const applicationReducer: Reducer<ApplicationState, CustomerAction> = (
  state = getInitialState(),
  action
) => {
  switch (action.type) {
    case APPLICATION_OPEN_MODAL: {
      const { name, ...rest } = action.payload;
      return {
        ...state,
        isModalOpen: true,
        modalName: name,
        modalOptions: rest,
      };
    }
    case APPLICATION_CLOSE_MODAL:
      return { ...state, isModalOpen: false, modalName: '', modalOptions: {} };
    case SET_PREVIOUS_PATH: {
      const { janeDeviceId, mixpanelDistinctId } = state;
      return {
        ...getInitialState(),
        janeDeviceId,
        mixpanelDistinctId,
      };
    }
    case APPLICATION_SET_JANE_DEVICE_ID: {
      const { janeDeviceId } = action.payload;
      return {
        ...state,
        janeDeviceId,
      };
    }
    case APPLICATION_SET_MIXPANEL_DISTINCT_ID: {
      const { mixpanelDistinctId } = action.payload;
      return {
        ...state,
        mixpanelDistinctId,
      };
    }
    case APPLICATION_ADD_GOOGLE_ANALYTICS_INSTANCE: {
      const { trackingId } = action.payload;
      if (
        !state.googleAnalyticsInstance ||
        state.googleAnalyticsInstance !== trackingId
      ) {
        Object.defineProperty(
          window,
          'JANE_GOOGLE_ANALYTICS_ENHANCED_ECOMMERCE_ENABLED',
          { value: true, writable: false }
        );

        initializeEcommerceGa(trackingId);
        window.addEventListener('message', function (event) {
          /// expects message as follows
          /// frameWindow.postMessage({ messageType: 'initializeGa', payload: { clientId: '123' }}, "*")
          if (
            event.data &&
            event.data.messageType === 'initializeGa' &&
            event.data.payload?.clientId
          ) {
            removeGa();
            initializeEcommerceGa(
              trackingId,
              event.data.payload?.clientId,
              event.data.payload?.referrer
            );
          }
        });

        return {
          ...state,
          googleAnalyticsInstance: trackingId,
        };
      }
    }
  }

  return state;
};

export const selectApplication = (state: AppState) => state.application;
